import { useState, useMemo, useEffect } from 'react';
import { useFormacao, type Formacao } from '@/context/FormacaoContext';
import { useSearchParams } from 'react-router-dom';
import { useCategoriasFormacao } from '@/hooks/useCategoriasFormacao';
import { estadoNormalizado } from '@/utils/estadoUtils';
import NovaFormacaoModal from './components/NovaFormacaoModal';
import DetalhesFormacaoModal from './components/DetalhesFormacaoModal';
import EditarFormacaoModal from './components/EditarFormacaoModal';
import ExportarAcoesModal from './components/ExportarAcoesModal';
import ImportarAcoesModal from './components/ImportarAcoesModal';

type AcaoSortKey = 'nome' | 'categoria' | 'dataInicio' | 'dataFim' | 'horas' | 'participantes' | 'estado';
type AcaoSortDir = 'asc' | 'desc';
type PeriodoFilter = '' | 'T1' | 'T2' | 'T3' | 'T4' | 'S1' | 'S2';

const ESTADO_ORDER: Record<string, number> = {
  'Em Curso': 0, 'Planeada': 1, 'Concluída': 2, 'Cancelada': 3,
};

const estadoCfg: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  'Concluída':  { label: 'Concluída',  bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Em Curso':   { label: 'Em Curso',   bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  'Planeada':   { label: 'Planeada',   bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
  'Cancelada':  { label: 'Cancelada',  bg: 'bg-rose-50',    text: 'text-rose-600',    dot: 'bg-rose-400'    },
};

// Extrai o ano de uma string de data YYYY-MM-DD sem depender de fuso horário
function getAnoFromDateStr(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length >= 1) return parseInt(parts[0], 10);
  return null;
}

// Extrai o mês de uma string de data YYYY-MM-DD (1-12)
function getMesFromDateStr(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length >= 2) return parseInt(parts[1], 10);
  return null;
}

function matchPeriodo(f: Formacao, periodo: PeriodoFilter, anoRef: number): boolean {
  if (!periodo) return true;
  const inicio = f.data_inicio;
  const fim = f.data_fim;
  // Uma formação pertence ao período se a data de início ou fim cair dentro dele
  function dataCaiNoPeriodo(dateStr: string | null): boolean {
    if (!dateStr) return false;
    const ano = getAnoFromDateStr(dateStr);
    const mes = getMesFromDateStr(dateStr);
    if (ano !== anoRef || mes === null) return false;
    switch (periodo) {
      case 'T1': return mes >= 1 && mes <= 3;
      case 'T2': return mes >= 4 && mes <= 6;
      case 'T3': return mes >= 7 && mes <= 9;
      case 'T4': return mes >= 10 && mes <= 12;
      case 'S1': return mes >= 1 && mes <= 6;
      case 'S2': return mes >= 7 && mes <= 12;
    }
    return false;
  }
  return dataCaiNoPeriodo(inicio) || dataCaiNoPeriodo(fim);
}

const modalidadeCfg: Record<string, { icon: string; color: string }> = {
  'Presencial':          { icon: 'ri-map-pin-line',   color: 'text-rose-500'   },
  'E-Learning':          { icon: 'ri-computer-line',  color: 'text-cyan-500'   },
  'Misto (Blended)':     { icon: 'ri-shuffle-line',   color: 'text-violet-500' },
  'Videoconferência':    { icon: 'ri-vidicon-line',   color: 'text-teal-500'   },
};

const PERIODOS: { key: PeriodoFilter; label: string }[] = [
  { key: 'T1', label: '1º Trimestre' },
  { key: 'T2', label: '2º Trimestre' },
  { key: 'T3', label: '3º Trimestre' },
  { key: 'T4', label: '4º Trimestre' },
  { key: 'S1', label: '1º Semestre' },
  { key: 'S2', label: '2º Semestre' },
];

const PAGE_SIZE = 15;

export default function AcoesFormacaoPage() {
  const { formacoes, participantes, loading, saving, eliminarFormacao, editarFormacao } = useFormacao();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('');
  const [anoFilter, setAnoFilter] = useState<string>(String(new Date().getFullYear()));
  const [periodoFilter, setPeriodoFilter] = useState<PeriodoFilter>('');
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // Ano pré-selecionado vindo do Plano via ?ano=&nova=1
  const [anoPreSelecionado, setAnoPreSelecionado] = useState<number | null>(null);
  const [estadoDropdownId, setEstadoDropdownId] = useState<string | null>(null);

  // Ler query params ao montar — abrir modal se ?nova=1
  useEffect(() => {
    const nova = searchParams.get('nova');
    const ano = searchParams.get('ano');
    if (nova === '1') {
      if (ano) {
        setAnoPreSelecionado(parseInt(ano));
        setAnoFilter(ano);
      }
      setShowModal(true);
      // Limpar os params da URL sem recarregar
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [formacaoDetalhes, setFormacaoDetalhes] = useState<Formacao | null>(null);
  const [formacaoEditar, setFormacaoEditar] = useState<Formacao | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importSuccess, setImportSuccess] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<AcaoSortKey>('dataInicio');
  const [sortDir, setSortDir] = useState<AcaoSortDir>('desc');

  const { categorias } = useCategoriasFormacao();

  const anosDisponiveis = useMemo(() => {
    const anos = new Set<string>();
    formacoes.forEach((f) => {
      const aInicio = getAnoFromDateStr(f.data_inicio);
      const aFim = getAnoFromDateStr(f.data_fim);
      if (aInicio !== null) anos.add(aInicio.toString());
      if (aFim !== null) anos.add(aFim.toString());
    });
    return Array.from(anos).sort((a, b) => Number(b) - Number(a));
  }, [formacoes]);

  // Determina o ano de referência para filtros de período
  const anoReferencia = useMemo(() => {
    if (anoFilter !== '') return Number(anoFilter);
    if (anosDisponiveis.length > 0) return Number(anosDisponiveis[0]);
    return new Date().getFullYear();
  }, [anoFilter, anosDisponiveis]);

  function handleSort(key: AcaoSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const filtered = useMemo(() => {
    const base = formacoes.filter((f) => {
      const matchSearch = search === '' ||
        f.nome.toLowerCase().includes(search.toLowerCase()) ||
        (f.entidade_formadora ?? '').toLowerCase().includes(search.toLowerCase());
      const matchEstado = estadoFilter === '' || estadoNormalizado(f.estado) === estadoNormalizado(estadoFilter);
      const matchCat = categoriaFilter === '' || f.categoria === categoriaFilter;
      const matchAno = anoFilter === '' || (() => {
        const anoNum = Number(anoFilter);
        const inicio = getAnoFromDateStr(f.data_inicio);
        const fim = getAnoFromDateStr(f.data_fim);
        if (inicio !== null && fim !== null) return anoNum >= inicio && anoNum <= fim;
        if (inicio !== null) return inicio === anoNum;
        if (fim !== null) return fim === anoNum;
        return false;
      })();
      const matchPeriodo = matchPeriodoFn(f, periodoFilter, anoReferencia);
      return matchSearch && matchEstado && matchCat && matchAno && matchPeriodo;
    });

    return [...base].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'nome': cmp = a.nome.localeCompare(b.nome); break;
        case 'categoria': cmp = (a.categoria ?? '').localeCompare(b.categoria ?? ''); break;
        case 'dataInicio': cmp = (a.data_inicio ?? '').localeCompare(b.data_inicio ?? ''); break;
        case 'dataFim': cmp = (a.data_fim ?? '').localeCompare(b.data_fim ?? ''); break;
        case 'horas': cmp = (a.duracao_horas ?? 0) - (b.duracao_horas ?? 0); break;
        case 'participantes': cmp = (a.total_participantes ?? 0) - (b.total_participantes ?? 0); break;
        case 'estado': cmp = (ESTADO_ORDER[a.estado] ?? ESTADO_ORDER[estadoNormalizado(a.estado)] ?? 99) - (ESTADO_ORDER[b.estado] ?? ESTADO_ORDER[estadoNormalizado(b.estado)] ?? 99); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [formacoes, search, estadoFilter, categoriaFilter, anoFilter, periodoFilter, anoReferencia, sortKey, sortDir]);

  // Reset para página 1 quando filtros ou ordenação mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [search, estadoFilter, categoriaFilter, anoFilter, periodoFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  const baseParaStats = useMemo(() => {
    if (anoFilter === '' && periodoFilter === '') return formacoes;
    return formacoes.filter((f) => {
      const anoNum = anoFilter !== '' ? Number(anoFilter) : null;
      const inicio = getAnoFromDateStr(f.data_inicio);
      const fim = getAnoFromDateStr(f.data_fim);
      let matchAno = true;
      if (anoNum !== null) {
        if (inicio !== null && fim !== null) matchAno = anoNum >= inicio && anoNum <= fim;
        else if (inicio !== null) matchAno = inicio === anoNum;
        else if (fim !== null) matchAno = fim === anoNum;
        else matchAno = false;
      }
      const matchPeriodo = matchPeriodoFn(f, periodoFilter, anoReferencia);
      return matchAno && matchPeriodo;
    });
  }, [formacoes, anoFilter, periodoFilter, anoReferencia]);

  const stats = {
    total: baseParaStats.length,
    concluidas: baseParaStats.filter((f) => estadoNormalizado(f.estado) === 'concluida').length,
    ativas: baseParaStats.filter((f) => estadoNormalizado(f.estado) === 'em_curso').length,
    planeadas: baseParaStats.filter((f) => estadoNormalizado(f.estado) === 'planeada').length,
    totalHoras: baseParaStats.reduce((s, f) => s + (f.duracao_horas ?? 0), 0),
  };

  const acaoHeaders: { key: AcaoSortKey | null; label: string; center?: boolean }[] = [
    { key: 'nome', label: 'Ação de Formação' },
    { key: 'categoria', label: 'Área' },
    { key: null, label: 'Modalidade' },
    { key: 'dataInicio', label: 'Início' },
    { key: 'dataFim', label: 'Fim' },
    { key: 'horas', label: 'Horas', center: true },
    { key: 'participantes', label: 'Part.', center: true },
    { key: null, label: 'Custo Total', center: true },
    { key: 'estado', label: 'Estado' },
    { key: null, label: '' },
  ];

  async function handleDelete(id: string) {
    await eliminarFormacao(id);
    setConfirmDelete(null);
  }

  function limparFiltros() {
    setAnoFilter('');
    setEstadoFilter('');
    setCategoriaFilter('');
    setSearch('');
    setPeriodoFilter('');
  }

  const temFiltrosAtivos = anoFilter !== '' || estadoFilter !== '' || categoriaFilter !== '' || search !== '' || periodoFilter !== '';

  // Gera array de números de página a mostrar
  function getPageNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  }

  return (
    <>
      <main className="flex-1 bg-slate-50/60">
        {/* Header */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-book-open-line text-white text-lg" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Ações de Formação</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">Gestão de todas as ações de formação do plano anual</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowExport(true)}
                className="whitespace-nowrap flex items-center gap-2 bg-zinc-100 text-zinc-700 text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-download-2-line text-sm" /></div>
                Exportar
              </button>
              <button
                onClick={() => setShowImport(true)}
                className="whitespace-nowrap flex items-center gap-2 bg-zinc-100 text-zinc-700 text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-2-line text-sm" /></div>
                Importar CSV
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                Nova Ação
              </button>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-5">
          {/* Banner importação */}
          {importSuccess !== null && (
            <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[13px] px-5 py-3 rounded-xl">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-sm" /></div>
                <span><strong>{importSuccess}</strong> ação(ões) importadas com sucesso!</span>
              </div>
              <button onClick={() => setImportSuccess(null)} className="whitespace-nowrap w-5 h-5 flex items-center justify-center text-emerald-500 hover:text-emerald-700 cursor-pointer">
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          )}
          {/* Stats */}
          {!loading && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                { label: 'Total de Ações',  value: stats.total,         icon: 'ri-book-open-line',     bg: 'bg-zinc-100 text-zinc-600'      },
                { label: 'Concluídas',      value: stats.concluidas,    icon: 'ri-checkbox-circle-line', bg: 'bg-emerald-50 text-emerald-600' },
                { label: 'Em Curso',        value: stats.ativas,        icon: 'ri-play-circle-line',   bg: 'bg-amber-50 text-amber-600'     },
                { label: 'Planeadas',       value: stats.planeadas,     icon: 'ri-calendar-todo-line', bg: 'bg-zinc-50 text-zinc-400'       },
                { label: 'Total de Horas',  value: `${stats.totalHoras}h`, icon: 'ri-time-line',      bg: 'bg-zinc-100 text-zinc-600'      },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                    <i className={`${s.icon} text-base`} />
                  </div>
                  <div>
                    <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                    <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filtros */}
          <div className="bg-white rounded-xl border border-zinc-100 p-4 space-y-3">
            {/* Linha 1: pesquisa + filtros dropdown */}
            <div className="flex gap-2.5 items-center flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                  <i className="ri-search-line text-sm" />
                </div>
                <input
                  type="text"
                  placeholder="Pesquisar ação ou entidade formadora..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                />
              </div>
              <select
                value={estadoFilter}
                onChange={(e) => setEstadoFilter(e.target.value)}
                className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
              >
                <option value="">Todos os estados</option>
                <option value="Em Curso">Em Curso</option>
                <option value="Planeada">Planeada</option>
                <option value="Concluída">Concluída</option>
                <option value="Cancelada">Cancelada</option>
              </select>
              <select
                value={categoriaFilter}
                onChange={(e) => setCategoriaFilter(e.target.value)}
                className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
              >
                <option value="">Todas as áreas</option>
                {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={anoFilter}
                onChange={(e) => { setAnoFilter(e.target.value); setPeriodoFilter(''); }}
                className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
              >
                <option value="">Todos os anos</option>
                {anosDisponiveis.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              {temFiltrosAtivos && (
                <button
                  onClick={limparFiltros}
                  className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-500 hover:text-zinc-700 bg-zinc-100 hover:bg-zinc-200 px-3 py-2.5 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-close-line text-xs" /></div>
                  Limpar filtros
                </button>
              )}
            </div>

            {/* Linha 2: filtros rápidos por período */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Período:</span>
              {PERIODOS.map((p) => {
                const ativo = periodoFilter === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => setPeriodoFilter(ativo ? '' : p.key)}
                    className={`whitespace-nowrap text-[12px] font-medium px-3 py-1.5 rounded-full transition-colors cursor-pointer border ${
                      ativo
                        ? 'bg-zinc-900 text-white border-zinc-900'
                        : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
              {periodoFilter !== '' && (
                <span className="text-[11px] text-zinc-400 ml-1">
                  em {anoFilter || anoReferencia}
                </span>
              )}
            </div>

            <p className="text-[12px] text-zinc-400">
              <span className="font-semibold text-zinc-600">{filtered.length}</span> ação(ões) encontrada(s)
              {filtered.length > PAGE_SIZE && (
                <span className="ml-1">— a mostrar <span className="font-semibold text-zinc-600">{Math.min(PAGE_SIZE, paginated.length)}</span> por página</span>
              )}
            </p>
          </div>

          {/* Conteúdo */}
          {loading ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
              </div>
              <p className="text-[13px] text-zinc-400">A carregar ações de formação...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-book-open-line text-4xl text-zinc-200" />
              </div>
              <p className="text-[14px] font-semibold text-zinc-400">
                {formacoes.length === 0 ? 'Ainda não há ações de formação registadas' : 'Nenhuma ação encontrada'}
              </p>
              {formacoes.length === 0 && (
                <button
                  onClick={() => setShowModal(true)}
                  className="whitespace-nowrap flex items-center gap-2 mt-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                  Criar primeira ação
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-zinc-100 overflow-auto max-h-[calc(100vh-260px)]">
              <table className="w-full min-w-[1024px]">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50">
                    {acaoHeaders.map((col, ci) => (
                      <th
                        key={ci}
                        onClick={() => col.key && handleSort(col.key)}
                        className={`px-5 py-3 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap select-none group ${
                          col.center ? 'text-center' : 'text-left'
                        } ${
                          col.key ? 'cursor-pointer hover:bg-zinc-100 transition-colors' : ''
                        } ${
                          sortKey === col.key ? 'text-zinc-700' : 'text-zinc-400'
                        }`}
                      >
                        <span className={`inline-flex items-center gap-1 ${col.center ? 'justify-center' : ''}`}>
                          {col.label}
                          {col.key && (
                            sortKey === col.key
                              ? <i className={`${sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'} text-[13px] text-zinc-700`} />
                              : <i className="ri-expand-up-down-line text-[11px] text-zinc-300 group-hover:text-zinc-400" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((acao, i) => {
                    const ec = estadoCfg[acao.estado] ?? estadoCfg['Planeada'];
                    const mc = modalidadeCfg[acao.modalidade] ?? { icon: 'ri-book-line', color: 'text-zinc-500' };
                    return (
                      <tr key={acao.id} onClick={() => setFormacaoDetalhes(acao)} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors cursor-pointer ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}>
                        <td className="px-5 py-3.5">
                          <p className="text-[13px] font-semibold text-zinc-800">{acao.nome}</p>
                          <p className="text-[11px] text-zinc-400 mt-0.5">{acao.entidade_formadora ?? '—'}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          {acao.categoria && (
                            <span className="text-[11px] font-medium text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-full">{acao.categoria}</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className={`${mc.icon} text-sm ${mc.color}`} />
                            </div>
                            <span className="text-[12px] text-zinc-600">{acao.modalidade}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          {acao.data_inicio ? (
                            <p className="text-[12px] text-zinc-700">{new Date(acao.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                          ) : <span className="text-[12px] text-zinc-300">—</span>}
                        </td>
                        <td className="px-5 py-3.5">
                          {acao.data_fim ? (
                            <p className="text-[12px] text-zinc-700">{new Date(acao.data_fim).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                          ) : <span className="text-[12px] text-zinc-300">—</span>}
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-[13px] font-bold text-zinc-700">{acao.duracao_horas}h</span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-[13px] font-bold text-zinc-700">{acao.total_participantes ?? 0}</span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-[13px] font-semibold text-zinc-700">
                            {(() => {
                              const custoTotal = participantes
                                .filter((p) => p.formacao_id === acao.id)
                                .reduce((s, p) => s + Number(p.custo || 0), 0);
                              return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(custoTotal);
                            })()}
                          </span>
                        </td>
                        <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const ec = estadoCfg[acao.estado] ?? estadoCfg['Planeada'];
                            const isOpen = estadoDropdownId === acao.id;
                            return (
                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEstadoDropdownId(isOpen ? null : acao.id);
                                  }}
                                  disabled={saving}
                                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${ec.bg} ${ec.text}`}
                                >
                                  <span className={`w-1.5 h-1.5 rounded-full ${ec.dot} flex-shrink-0`} />
                                  {ec.label}
                                  <div className="w-3 h-3 flex items-center justify-center opacity-60">
                                    <i className="ri-arrow-down-s-line text-xs" />
                                  </div>
                                </button>
                                {isOpen && (
                                  <div className="absolute z-20 mt-1 left-0 bg-white border border-zinc-200 rounded-xl shadow-lg py-1 min-w-[140px]">
                                    {Object.entries(estadoCfg).map(([key, cfg]) => (
                                      <button
                                        key={key}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          setEstadoDropdownId(null);
                                          if (key !== acao.estado) {
                                            await editarFormacao(acao.id, { estado: key });
                                          }
                                        }}
                                        disabled={saving}
                                        className={`w-full text-left px-3 py-1.5 text-[11px] font-semibold flex items-center gap-2 hover:bg-zinc-50 transition-colors cursor-pointer ${acao.estado === key ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      >
                                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                                        <span className={cfg.text}>{cfg.label}</span>
                                        {acao.estado === key && <i className="ri-check-line text-xs ml-auto" />}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                          {confirmDelete === acao.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(acao.id)}
                                disabled={saving}
                                className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 hover:bg-rose-600 px-2.5 py-1 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                              >
                                Confirmar
                              </button>
                              <button
                                onClick={() => setConfirmDelete(null)}
                                className="whitespace-nowrap text-[11px] font-medium text-zinc-500 bg-zinc-100 hover:bg-zinc-200 px-2.5 py-1 rounded-lg transition-colors cursor-pointer"
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => { setFormacaoEditar(acao); }}
                                className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
                                title="Editar"
                              >
                                <i className="ri-edit-line text-sm" />
                              </button>
                              <button
                                onClick={() => setConfirmDelete(acao.id)}
                                className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
                                title="Eliminar"
                              >
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Paginação */}
              {filtered.length > PAGE_SIZE && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-100 bg-zinc-50/50">
                  <p className="text-[12px] text-zinc-500">
                    A mostrar <span className="font-semibold text-zinc-700">{(currentPage - 1) * PAGE_SIZE + 1}</span>–<span className="font-semibold text-zinc-700">{Math.min(currentPage * PAGE_SIZE, filtered.length)}</span> de <span className="font-semibold text-zinc-700">{filtered.length}</span>
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-left-s-line text-base" />
                    </button>
                    {getPageNumbers().map((page, idx) => (
                      typeof page === 'string' ? (
                        <span key={`dots-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px] text-zinc-400">…</span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-[12px] font-medium transition-colors cursor-pointer ${
                            currentPage === page
                              ? 'bg-zinc-900 text-white'
                              : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-right-s-line text-base" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {showModal && (
        <NovaFormacaoModal
          onClose={() => { setShowModal(false); setAnoPreSelecionado(null); }}
          onSuccess={() => { setShowModal(false); setAnoPreSelecionado(null); }}
          anoPreSelecionado={anoPreSelecionado}
        />
      )}

      {formacaoDetalhes && (
        <DetalhesFormacaoModal
          formacao={formacaoDetalhes}
          onClose={() => setFormacaoDetalhes(null)}
        />
      )}

      {showExport && (
        <ExportarAcoesModal
          formacoes={formacoes}
          onClose={() => setShowExport(false)}
        />
      )}

      {showImport && (
        <ImportarAcoesModal
          onClose={() => setShowImport(false)}
          onSuccess={(count) => {
            setShowImport(false);
            setImportSuccess(count);
          }}
        />
      )}

      {formacaoEditar && (
        <EditarFormacaoModal
          formacao={formacaoEditar}
          onClose={() => setFormacaoEditar(null)}
          onSuccess={(updated) => {
            setFormacaoEditar(null);
            // Atualizar também o formacaoDetalhes se estiver aberto para a mesma ação
            if (formacaoDetalhes?.id === updated.id) {
              setFormacaoDetalhes((prev) => prev ? { ...prev, ...updated } : null);
            }
          }}
        />
      )}
    </>
  );
}

// Helper extraído fora do componente para evitar dependências cíclicas no useMemo
function matchPeriodoFn(f: Formacao, periodo: PeriodoFilter, anoRef: number): boolean {
  if (!periodo) return true;
  function dataCaiNoPeriodo(dateStr: string | null): boolean {
    if (!dateStr) return false;
    const parts = dateStr.split('-');
    const ano = parts.length >= 1 ? parseInt(parts[0], 10) : null;
    const mes = parts.length >= 2 ? parseInt(parts[1], 10) : null;
    if (ano !== anoRef || mes === null) return false;
    switch (periodo) {
      case 'T1': return mes >= 1 && mes <= 3;
      case 'T2': return mes >= 4 && mes <= 6;
      case 'T3': return mes >= 7 && mes <= 9;
      case 'T4': return mes >= 10 && mes <= 12;
      case 'S1': return mes >= 1 && mes <= 6;
      case 'S2': return mes >= 7 && mes <= 12;
    }
    return false;
  }
  return dataCaiNoPeriodo(f.data_inicio) || dataCaiNoPeriodo(f.data_fim);
}